import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Badge,
  Collapse,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  AccessTime as AttendanceIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  AccountCircle,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Security as WhitelistIcon,
  ExpandLess,
  ExpandMore,
  MyLocation as MyLocationIcon,
  FiberManualRecord,
  EventNote,
  AssignmentTurnedIn,
  Assessment,
  MoreTime,
  Schedule,
  CalendarMonth,
  Rule,
  CardMembership,
  Tune,
  AutoFixHigh,
  CameraAlt,
  Fingerprint,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Build as BuildIcon,
  AttachMoney as BillingIcon,
  VisibilityOff,
  Visibility,
  VerifiedUser as VerifiedUserIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  AccountBalance as PayrollIcon,
  AccountBalanceWallet as WalletIcon,
  GpsFixed as GpsFixedIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { authApi } from '../../api/authApi';
import { employeeApi } from '../../api/employeeApi';
import { notificationApi } from '../../api/notificationApi';
import { logout } from '../../store/slices/authSlice';
import { AppDispatch, RootState } from '../../store/store';
import { NotificationItem } from '../../types';
import { endSession } from '../../utils/endSession';
import { getNotificationRoute } from '../../utils/notificationUtils';
import { getUploadsBaseUrl } from '../../config/runtimeConfig';
import HelpMenu from '../Help/HelpMenu';
import { FEATURE_KEYS, hasFeatureAccess, hasPermissionAccess, isTenantAdminUser, userHasAnyAssignedPermission } from '../../utils/permissionAccess';
import LanguagePreferenceSelect from '../LanguagePreferenceSelect';
import { useLanguage } from '../../i18n/LocalizationContext';


const drawerWidth = 270;


const getUploadedFileUrl = (filePath?: string | null) => {
  if (!filePath) return undefined;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  return `${getUploadsBaseUrl()}/${filePath.replace(/^\/+/, '')}`;
};


// ─── Menu Item Type ───
interface MenuItemType {
  text: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItemType[];
  dividerBefore?: boolean;
  permission?: string;
  anyPermission?: string[];
  roles?: string[];
  featureKey?: string;
  hideForTenantAdmin?: boolean;
}


// ─── Filter menu items by user permissions ───
const filterMenuByPermissions = (
  items: MenuItemType[],
  userPermissions: string[],
  userRoles: string[],
  enabledFeatures?: string[]
): MenuItemType[] => {
  const canAccessPermission = (requiredPermission: string) =>
    hasPermissionAccess(requiredPermission, userPermissions, userRoles);
  const isTenantAdmin = isTenantAdminUser(userRoles);

  return items
    .map((item) => {
      if (!hasFeatureAccess(item.featureKey, enabledFeatures)) return null;
      if (item.hideForTenantAdmin && isTenantAdmin) return null;

      const filteredChildren = item.children
        ? filterMenuByPermissions(item.children, userPermissions, userRoles, enabledFeatures)
        : undefined;


      if (item.children && (!filteredChildren || filteredChildren.length === 0)) return null;

      if (item.permission && !canAccessPermission(item.permission)) return null;

      if (item.anyPermission && !item.anyPermission.some((p) => canAccessPermission(p)))
        return null;

      if (item.roles && !item.roles.some((role) => userRoles.includes(role))) return null;

      return { ...item, children: filteredChildren };
    })
    .filter(Boolean) as MenuItemType[];
};


// ─── Helper: check if any descendant path is active ───
const isChildActive = (item: MenuItemType, pathname: string): boolean => {
  if (item.path && pathname.startsWith(item.path)) return true;
  if (item.children) return item.children.some((child) => isChildActive(child, pathname));
  return false;
};


// ─── Level-based styling ───
const getLevelStyles = (level: number, isActive: boolean, isChildActive: boolean = false) => {
  const base = {
    py: 0.4,
    minHeight: 36,
    borderRadius: 1.5,
    mx: 0.5,
    mb: 0.2,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  };


  if (level === 0) {
    if (isActive) {
      return {
        ...base,
        py: 0.7,
        minHeight: 44,
        mx: 1,
        bgcolor: 'primary.main',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)',
        '& .MuiListItemIcon-root': { color: '#fff' },
        '&:hover': { bgcolor: 'primary.dark' },
      };
    }
    if (isChildActive) {
      return {
        ...base,
        py: 0.7,
        minHeight: 44,
        mx: 1,
        bgcolor: 'rgba(102, 126, 234, 0.08)',
        '& .MuiListItemIcon-root': { color: 'primary.main' },
        '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.12)' },
      };
    }
    return {
      ...base,
      py: 0.7,
      minHeight: 44,
      mx: 1,
      '&:hover': {
        bgcolor: 'rgba(0, 0, 0, 0.04)',
        '& .MuiListItemIcon-root': { color: 'text.secondary' },
      },
    };
  }


  if (level === 1) {
    if (isActive) {
      return {
        ...base,
        pl: 2.5,
        ml: 1,
        bgcolor: 'rgba(102, 126, 234, 0.1)',
        borderLeft: '3px solid',
        borderColor: 'primary.main',
        borderRadius: '0 8px 8px 0',
        '& .MuiListItemIcon-root': { color: 'primary.main' },
        '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.15)' },
      };
    }
    return {
      ...base,
      pl: 2.5,
      ml: 1,
      borderLeft: '3px solid transparent',
      borderRadius: '0 8px 8px 0',
      '&:hover': {
        bgcolor: 'rgba(0, 0, 0, 0.03)',
        borderColor: 'rgba(102, 126, 234, 0.3)',
      },
    };
  }


  if (level === 2) {
    return {
      ...base,
      pl: 4,
      ...(isActive
        ? {
            bgcolor: 'rgba(102, 126, 234, 0.08)',
            color: 'primary.main',
            fontWeight: 600,
          }
        : {
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
          }),
    };
  }


  return {
    ...base,
    pl: 2 + level * 1.5,
    ...(isActive && {
      bgcolor: 'rgba(102, 126, 234, 0.06)',
      color: 'primary.main',
    }),
  };
};


// ─── Recursive Sidebar Menu Item ───
interface SidebarMenuItemProps {
  item: MenuItemType;
  level: number;
  pathname: string;
  navigate: (path: string) => void;
  // Accordion controlled props (only used at level 0)
  isOpen?: boolean;
  onToggle?: (text: string) => void;
}


const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  level,
  pathname,
  navigate,
  isOpen,
  onToggle,
}) => {
  const { t } = useLanguage();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const childActive = isChildActive(item, pathname);
  const selfActive = item.path
    ? pathname === item.path || (item.path !== '/dashboard' && pathname.startsWith(item.path + '/'))
    : false;
  const isActive = selfActive || (hasChildren && childActive);

  // Local open state is used for nested levels (level >= 1)
  const [localOpen, setLocalOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setLocalOpen(true);
  }, [childActive]);

  // Level 0 uses controlled open from parent (accordion); deeper levels use local state
  const open = level === 0 && isOpen !== undefined ? isOpen : localOpen;

  const handleClick = useCallback(() => {
    if (hasChildren) {
      if (level === 0 && onToggle) {
        // Accordion: notify parent to handle open/close
        onToggle(item.text);
      } else {
        // Nested levels toggle independently
        setLocalOpen((prev) => !prev);
      }
    } else if (item.path) {
      navigate(item.path);
    }
  }, [hasChildren, item.path, item.text, navigate, level, onToggle]);


  const expandIcon = hasChildren ? (
    open ? (
      <ExpandLess sx={{ fontSize: 18, color: isActive ? 'primary.main' : 'text.disabled' }} />
    ) : (
      <ExpandMore sx={{ fontSize: 18, color: isActive ? 'primary.main' : 'text.disabled' }} />
    )
  ) : null;


  const leafIcon =
    !hasChildren && level >= 2 ? (
      <FiberManualRecord
        sx={{
          fontSize: 6,
          color: selfActive ? 'primary.main' : 'text.disabled',
        }}
      />
    ) : null;


  const textStyle = useMemo(
    () => ({
      '& .MuiTypography-root': {
        fontSize: level === 0 ? '0.9rem' : level === 1 ? '0.84rem' : '0.8rem',
        fontWeight: isActive ? 600 : level === 0 ? 500 : 400,
        color:
          selfActive && level === 0
            ? '#fff'
            : selfActive
            ? 'primary.main'
            : hasChildren && childActive
            ? 'primary.main'
            : 'text.secondary',
        lineHeight: 1.4,
      },
    }),
    [level, isActive, selfActive, hasChildren, childActive]
  );


  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          sx={getLevelStyles(level, selfActive, hasChildren && childActive && !selfActive)}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: 34,
                color: isActive ? 'primary.main' : 'text.disabled',
              }}
            >
              {item.icon}
            </ListItemIcon>
          )}
          {leafIcon && <ListItemIcon sx={{ minWidth: 20 }}>{leafIcon}</ListItemIcon>}

          <ListItemText primary={t(item.text)} sx={textStyle} />

          {expandIcon}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map((child) => (
              <SidebarMenuItem
                key={child.text}
                item={child}
                level={level + 1}
                pathname={pathname}
                navigate={navigate}
                // No accordion props passed to nested items — they manage themselves
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};


// ─── Menu definition ───
const menuItems: MenuItemType[] = [
  { text: 'navigation.dashboard', icon: <DashboardIcon fontSize="small" />, path: '/dashboard', anyPermission: ['ViewDashboard', 'ViewEmployeeDashboard'], featureKey: FEATURE_KEYS.DASHBOARD },
  { text: 'navigation.reports', icon: <Assessment fontSize="small" />, path: '/reports', anyPermission: ['ViewReports', 'ViewOwnReports'], featureKey: FEATURE_KEYS.REPORTS },
  { text: 'navigation.vendorBgv', icon: <VerifiedUserIcon fontSize="small" />, path: '/bgv/assigned-cases', permission: 'ViewAssignedBGV', roles: ['BGVVendor'], featureKey: FEATURE_KEYS.BACKGROUND_VERIFICATION },
  { text: 'navigation.employees', icon: <PeopleIcon fontSize="small" />, path: '/employees', permission: 'ViewEmployee', featureKey: FEATURE_KEYS.EMPLOYEES },
  {
    text: 'navigation.attendanceShift',
    icon: <AttendanceIcon fontSize="small" />,
    dividerBefore: true,
    featureKey: FEATURE_KEYS.ATTENDANCE,
    anyPermission: ['ViewAttendance', 'ViewAttendanceRecords', 'ViewRegularizationRequests', 'ViewAttendanceReports', 'ViewShifts', 'ManageShifts', 'ManualOT', 'ManageBiometric'],
    children: [
      { text: 'navigation.attendanceRecords', icon: <EventNote sx={{ fontSize: 18 }} />, path: '/attendance/records', permission: 'ViewAttendanceRecords' },
      {
        text: 'navigation.attendanceRegularizationRequest',
        icon: <PersonIcon sx={{ fontSize: 18 }} />,
        path: '/attendance/regularization-request',
        anyPermission: ['ViewAttendance', 'ViewAttendanceRecords'],
      },
      { text: 'navigation.registerFace', icon: <PersonIcon sx={{ fontSize: 18 }} />, path: '/attendance/face-registration', permission: 'ViewAttendance' },
      // { text: 'navigation.faceCheckInOut', icon: <CameraAlt sx={{ fontSize: 18 }} />, path: '/attendance/face-punch', permission: 'ViewAttendance' },
      { text: 'navigation.faceKiosk', icon: <Fingerprint sx={{ fontSize: 18 }} />, path: '/attendance/kiosk', permission: 'ManageShifts' },
      { text: 'navigation.regularizationRequests', icon: <AssignmentTurnedIn sx={{ fontSize: 18 }} />, path: '/attendance/regularization', permission: 'ViewRegularizationRequests' },
      { text: 'navigation.attendanceReport', icon: <Assessment sx={{ fontSize: 18 }} />, path: '/attendance/report', permission: 'ViewAttendanceReports' },
      { text: 'navigation.manualOt', icon: <MoreTime sx={{ fontSize: 18 }} />, path: '/attendance/manual-ot', permission: 'ManualOT' },
      { text: 'navigation.shiftManagement', icon: <Schedule sx={{ fontSize: 18 }} />, path: '/attendance/shift', permission: 'ViewShifts' },
      { text: 'navigation.rules', icon: <Rule sx={{ fontSize: 18 }} />, path: '/attendance/rules', permission: 'ManageShifts' },
      { text: 'navigation.biometric', icon: <Fingerprint sx={{ fontSize: 18 }} />, path: '/attendance/biometric', permission: 'ManageBiometric' },
    ],
  },
  {
    text: 'navigation.leaveManagement',
    icon: <EventNote sx={{ fontSize: 18 }} />,
    featureKey: FEATURE_KEYS.LEAVE,
    anyPermission: ['ViewLeave', 'ViewLeaveRequests', 'ManageLeave', 'ApproveLeave', 'ViewLeaveUsageReport'],
    children: [
      { text: 'navigation.myLeave', icon: <EventNote sx={{ fontSize: 18 }} />, path: '/leave/my-leave', permission: 'ViewLeave', hideForTenantAdmin: true },
      { text: 'navigation.leaveRequests', icon: <AssignmentTurnedIn sx={{ fontSize: 18 }} />, path: '/leave/requests', anyPermission: ['ViewLeaveRequests', 'ApproveLeave'] },
      { text: 'navigation.assignLeave', icon: <CardMembership sx={{ fontSize: 18 }} />, path: '/leave/assign', permission: 'ManageLeave' },
      { text: 'navigation.manageHolidays', icon: <CalendarMonth sx={{ fontSize: 18 }} />, path: '/leave/holidays', permission: 'ManageLeave' },
      { text: 'navigation.leaveEntitlements', icon: <CategoryIcon sx={{ fontSize: 18 }} />, path: '/leave/entitlements', permission: 'ManageLeave' },
      { text: 'navigation.leaveUsageReport', icon: <Assessment sx={{ fontSize: 18 }} />, path: '/leave/usage-report', permission: 'ViewLeaveUsageReport' },
      { text: 'navigation.leavePeriod', icon: <CalendarMonth sx={{ fontSize: 18 }} />, path: '/attendance/leave-period', permission: 'ManageLeave' },
      { text: 'navigation.leaveTypes', icon: <CategoryIcon sx={{ fontSize: 18 }} />, path: '/attendance/leave-types', permission: 'ManageLeave' },
      { text: 'navigation.attendanceCalendar', icon: <CalendarMonth sx={{ fontSize: 18 }} />, path: '/leave/leave-calendar', anyPermission: ['ViewLeave'] },
    ],
  },
  // {
  //   text: 'navigation.activityLogs',
  //   icon: <HistoryIcon fontSize="small" />,
  //   permission: 'ViewActivityLogs',
  //   featureKey: FEATURE_KEYS.ACTIVITY_LOGS,
  //   children: [
  //     //{ text: 'navigation.viewAllPunches', icon: <ReceiptIcon sx={{ fontSize: 18 }} />, path: '/activity-logs/view-all-punches', permission: 'ViewActivityLogs' },
  //     { text: 'navigation.punchSettings', icon: <Fingerprint sx={{ fontSize: 18 }} />, path: '/attendance/punch-settings', permission: 'ManageShifts' },
  //   ],
  // },

  {
    text: 'navigation.punchSettings',
    icon: <Fingerprint fontSize="small" />,
    permission: 'ViewActivityLogs',
    path: '/attendance/punch-settings',
    featureKey: FEATURE_KEYS.TOOLS,
  },
  {
    text: 'navigation.locationManagement',
    icon: <LocationIcon fontSize="small" />,
    dividerBefore: true,
    featureKey: FEATURE_KEYS.LOCATION,
    anyPermission: ['ViewLocation', 'ManageLocation'],
    children: [
      { text: 'navigation.locations', icon: <MyLocationIcon sx={{ fontSize: 18 }} />, path: '/locations', permission: 'ViewLocation' },
      { text: 'navigation.categories', icon: <CategoryIcon sx={{ fontSize: 18 }} />, path: '/location-categories', permission: 'ViewLocation' },
      { text: 'navigation.whitelist', icon: <WhitelistIcon sx={{ fontSize: 18 }} />, path: '/location-whitelist', permission: 'ManageLocation' },
    ],
  },
  {
    text: 'navigation.geoLocationTracking',
    icon: <GpsFixedIcon fontSize="small" />,
    featureKey: FEATURE_KEYS.GEO_LOCATION,
    anyPermission: ['ViewGeoLocation', 'ViewOwnGeoLocation'],
    children: [
      { text: 'navigation.locationHistory', icon: <HistoryIcon sx={{ fontSize: 18 }} />, path: '/geo-location/history', anyPermission: ['ViewGeoLocation', 'ViewOwnGeoLocation'] },
      { text: 'navigation.locationReport', icon: <Assessment sx={{ fontSize: 18 }} />, path: '/geo-location/report', anyPermission: ['ViewGeoLocation', 'ViewOwnGeoLocation'] },
    ],
  },
  {
    text: 'navigation.billing',
    icon: <BillingIcon fontSize="small" />,
    path: '/billing',
    permission: 'ViewBilling',
    featureKey: FEATURE_KEYS.BILLING,
  },
  {
    text: 'navigation.notifications',
    icon: <NotificationsIcon fontSize="small" />,
    path: '/notifications',
    permission: 'ViewNotifications',
    featureKey: FEATURE_KEYS.NOTIFICATIONS,
  },
  {
    text: 'navigation.payroll',
    icon: <PayrollIcon fontSize="small" />,
    featureKey: FEATURE_KEYS.PAYROLL,
    anyPermission: ['ViewPayroll', 'ManagePayroll', 'ProcessPayroll', 'ViewMySalary', 'ViewMyPayslip', 'ViewMyTaxDeclaration', 'ViewLoan', 'ViewMyLoan', 'RequestLoan'],
    children: [
      { text: 'navigation.dashboard', icon: <DashboardIcon sx={{ fontSize: 18 }} />, path: '/payroll/dashboard', anyPermission: ['ViewPayroll', 'ManagePayroll'] },
      { text: 'navigation.payrollRuns', icon: <EventNote sx={{ fontSize: 18 }} />, path: '/payroll/runs', anyPermission: ['ViewPayroll', 'ProcessPayroll'] },
      { text: 'navigation.salaryComponents', icon: <CategoryIcon sx={{ fontSize: 18 }} />, path: '/payroll/salary-components', permission: 'ManagePayroll' },
      { text: 'navigation.groupAssignments', icon: <PeopleIcon sx={{ fontSize: 18 }} />, path: '/payroll/group-assignments', permission: 'ManagePayroll' },
      { text: 'navigation.loansAdvances', icon: <WalletIcon sx={{ fontSize: 18 }} />, path: '/loans', permission: 'ViewLoan' },
      { text: 'navigation.taxDeclarations', icon: <DescriptionIcon sx={{ fontSize: 18 }} />, path: '/payroll/tax-declarations', anyPermission: ['ViewPayroll', 'ManagePayroll'] },
      { text: 'navigation.payslips', icon: <ReceiptIcon sx={{ fontSize: 18 }} />, path: '/payroll/payslips', permission: 'ViewPayroll' },
      { text: 'navigation.settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/payroll/settings', permission: 'ManagePayroll' },
      { text: 'navigation.mySalary', icon: <WalletIcon sx={{ fontSize: 18 }} />, path: '/payroll/my-salary', permission: 'ViewMySalary' },
      { text: 'navigation.myPayslips', icon: <ReceiptIcon sx={{ fontSize: 18 }} />, path: '/payroll/my-payslips', permission: 'ViewMyPayslip' },
      { text: 'navigation.myTaxDeclarations', icon: <DescriptionIcon sx={{ fontSize: 18 }} />, path: '/payroll/my-tax-declarations', permission: 'ViewMyTaxDeclaration' },
      { text: 'navigation.myLoans', icon: <WalletIcon sx={{ fontSize: 18 }} />, path: '/loans/my', anyPermission: ['ViewMyLoan', 'RequestLoan'] },
    ],
  },
  {
    text: 'navigation.backgroundVerification',
    icon: <VerifiedUserIcon fontSize="small" />,
    featureKey: FEATURE_KEYS.BACKGROUND_VERIFICATION,
    anyPermission: ['ViewBGV', 'ManageBGV'],
    children: [
      { text: 'navigation.dashboard', icon: <DashboardIcon sx={{ fontSize: 18 }} />, path: '/bgv/dashboard', anyPermission: ['ViewBGV', 'ManageBGV'] },
      { text: 'navigation.verificationCases', icon: <AssignmentTurnedIn sx={{ fontSize: 18 }} />, path: '/bgv/cases', anyPermission: ['ViewBGV', 'ManageBGV'] },
      //{ text: 'Create Verification', icon: <EventNote sx={{ fontSize: 18 }} />, path: '/bgv/create', permission: 'ManageBGV' },
      { text: 'navigation.vendorsSettings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/bgv/vendors', permission: 'ManageBGV' },
    ],
  },
  {
    text: 'navigation.settings',
    icon: <SettingsIcon fontSize="small" />,
    dividerBefore: true,
    featureKey: FEATURE_KEYS.SETTINGS,
    anyPermission: ['ManageSettings', 'ManageRoles', 'ManagePermissions', 'ViewMaster'],
    children: [
      { text: 'navigation.roles', icon: <LockIcon sx={{ fontSize: 18 }} />, path: '/settings/roles', permission: 'ManageRoles' },
      // { text: 'navigation.permissions', icon: <LockIcon sx={{ fontSize: 18 }} />, path: '/settings/permissions', permission: 'ManagePermissions' },
      { text: 'navigation.documentManagement', icon: <DescriptionIcon sx={{ fontSize: 18 }} />, path: '/document-management-settings', permission: 'ManageSettings' },
      { text: 'navigation.generalDefaultSetting', icon: <Tune sx={{ fontSize: 18 }} />, path: '/genral-default-setting', permission: 'ManageSettings' },
      { text: 'navigation.accountSettings', icon: <PersonIcon sx={{ fontSize: 18 }} />, path: '/settings/account-management', permission: 'ManageSettings' },
    ],
  },
  {
    text: 'navigation.tools',
    icon: <BuildIcon fontSize="small" />,
    permission: 'ManageTools',
    path: '/admin/announcements',
    featureKey: FEATURE_KEYS.TOOLS,
  },
];


const DashboardLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentUserProfileImage, setCurrentUserProfileImage] = useState<string | null>(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [bellNotifications, setBellNotifications] = useState<NotificationItem[]>([]);
  const [isBellLoading, setIsBellLoading] = useState(false);
  const [isMarkAllReadLoading, setIsMarkAllReadLoading] = useState(false);

  const truncateText = (text: string, maxLength: number = 60): string => {
  if (!text) return '';
  return text.length > maxLength
    ? text.substring(0, maxLength) + '...'
    : text;
};

  // ─── Accordion state: tracks which top-level menu item is open (by text key) ───
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  // ─── Filter menu items based on user permissions ───
  const userRoles = useMemo(
    () => [...(user?.roles ?? []), ...(user?.roleName ? [user.roleName] : [])],
    [user?.roles, user?.roleName]
  );

  const visibleMenuItems = useMemo(
    () => filterMenuByPermissions(menuItems, user?.permissions ?? [], userRoles, user?.enabledFeatures),
    [user?.permissions, userRoles, user?.enabledFeatures]
  );
  const hasAnyAssignedPermission = userHasAnyAssignedPermission(user);
  const showNavigation = visibleMenuItems.length > 0;

  // ─── Auto-expand the active parent menu on route change ───
  useEffect(() => {
    const activeParent = visibleMenuItems.find(
      (item) => item.children && isChildActive(item, location.pathname)
    );
    if (activeParent) {
      setOpenMenuKey(activeParent.text);
    }
  }, [location.pathname, visibleMenuItems]);

  // ─── Accordion toggle: open clicked item, close all others ───
  const handleMenuToggle = useCallback((text: string) => {
    setOpenMenuKey((prev) => (prev === text ? null : text));
  }, []);

  const handleDrawerToggle = () => setOpen(!open);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const isNotificationOpen = Boolean(notificationAnchorEl);

  useEffect(() => {
    let active = true;

    const loadCurrentUserProfileImage = async () => {
      try {
        const currentEmployee = await employeeApi.getCurrentEmployee();
        const profile = await employeeApi.getProfile(currentEmployee.id);
        if (active) {
          // profileImageUrl is a browser-ready (signed) URL; profileImagePath is the raw
          // private-blob path which the browser cannot load directly.
          setCurrentUserProfileImage(profile.profileImageUrl || null);
        }
      } catch {
        if (active) {
          setCurrentUserProfileImage(null);
        }
      }
    };

    if (user && hasAnyAssignedPermission) {
      loadCurrentUserProfileImage();
    } else {
      setCurrentUserProfileImage(null);
    }

    return () => {
      active = false;
    };
  }, [user, hasAnyAssignedPermission]);

  const loadUnreadNotificationCount = useCallback(async () => {
    if (!hasAnyAssignedPermission) {
      setUnreadNotificationCount(0);
      return;
    }

    try {
      const response = await notificationApi.getMyNotifications({ pageNumber: 1, pageSize: 1 });
      setUnreadNotificationCount(response.unreadCount ?? 0);
    } catch {
      setUnreadNotificationCount(0);
    }
  }, [hasAnyAssignedPermission]);

  const loadBellNotifications = useCallback(async () => {
    if (!hasAnyAssignedPermission) {
      setBellNotifications([]);
      setUnreadNotificationCount(0);
      return;
    }

    setIsBellLoading(true);
    try {
      const response = await notificationApi.getMyNotifications({ pageNumber: 1, pageSize: 8 });
      setBellNotifications(response.items ?? []);
      setUnreadNotificationCount(response.unreadCount ?? 0);
    } catch {
      setBellNotifications([]);
    } finally {
      setIsBellLoading(false);
    }
  }, [hasAnyAssignedPermission]);

  useEffect(() => {
    if (!user || !hasAnyAssignedPermission) {
      setUnreadNotificationCount(0);
      return;
    }

    void loadUnreadNotificationCount();
    const intervalId = window.setInterval(() => {
      void loadUnreadNotificationCount();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [user, hasAnyAssignedPermission, loadUnreadNotificationCount]);

  const formatNotificationAge = (createdAt: string): string => {
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    const diffMs = Math.max(0, now - created);
    const minutes = Math.floor(diffMs / 60000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('notifications.hoursAgo', { count: hours });

    const days = Math.floor(hours / 24);
    return t('notifications.daysAgo', { count: days });
  };

  const handleBellOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    void loadBellNotifications();
  };

  const handleBellClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleBellNotificationClick = async (item: NotificationItem) => {
    if (!item.isRead) {
      try {
        await notificationApi.markAsRead(item.id);
        setBellNotifications((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
        );
        setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
      } catch {
        toast.error(t('notifications.updateFailed'));
      }
    }

    handleBellClose();
    navigate(getNotificationRoute(item));
  };

  const handleMarkAllRead = async () => {
    const unreadItems = bellNotifications.filter((item) => !item.isRead);
    if (unreadItems.length === 0) return;

    setIsMarkAllReadLoading(true);
    try {
      await Promise.all(unreadItems.map((item) => notificationApi.markAsRead(item.id)));
      setBellNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadNotificationCount((prev) => Math.max(0, prev - unreadItems.length));
    } catch {
      toast.error(t('notifications.markAllFailed'));
    } finally {
      setIsMarkAllReadLoading(false);
    }
  };

  const handleLogout = async () => {
    await endSession();
    dispatch(logout());
    navigate('/login');
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return t('validation.passwordLength');
    if (!/[A-Z]/.test(password)) return t('validation.passwordUppercase');
    if (!/[a-z]/.test(password)) return t('validation.passwordLowercase');
    if (!/[0-9]/.test(password)) return t('validation.passwordNumber');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      return t('validation.passwordSpecial');
    return null;
  };

  const resetChangePasswordState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const handleOpenChangePassword = () => {
    handleMenuClose();
    resetChangePasswordState();
    setChangePasswordOpen(true);
  };

  const handleOpenLanguagePreferences = () => {
    handleMenuClose();
    setLanguageDialogOpen(true);
  };

  const handleCloseLanguagePreferences = () => {
    setLanguageDialogOpen(false);
  };

  const handleSaveLanguagePreferences = () => {
    setLanguageDialogOpen(false);
    toast.success(t('language.saved'));
  };

  const handleCloseChangePassword = () => {
    if (changePasswordLoading) return;
    setChangePasswordOpen(false);
    resetChangePasswordState();
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('validation.requiredFields'));
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('validation.passwordMatch'));
      return;
    }

    setChangePasswordLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setChangePasswordOpen(false);
      resetChangePasswordState();
      // Changing the password revokes every session opened with the old one, this browser included,
      // so sign in again rather than carry on with tokens the server has just invalidated.
      toast.success(t('messages.passwordChangedSignInAgain'));
      dispatch(logout());
      navigate('/login');
      return;
    } catch (err: any) {
      const message = err?.response?.data?.message || t('messages.passwordChangeFailed');
      if (message === 'Current password is incorrect') {
        toast.error(t('messages.invalidCurrentPassword'));
      }
      setPasswordError(message);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleProfile = async () => {
    handleMenuClose();

    const userEmail = user?.email?.trim().toLowerCase();
    const isAdminUser = !!user?.roles?.some((role) => role.toLowerCase().includes('admin'));
    const canManageEmployees = !!user?.permissions?.includes('ViewEmployee');

    const goToProfileRoute = (employeeId: string) => {
      if (isAdminUser || canManageEmployees) {
        navigate(`/admin/employee/manage/${employeeId}/basic`);
        return;
      }
      navigate(`/my-profile/${employeeId}/basic`);
    };

    try {
      try {
        const currentEmployee = await employeeApi.getCurrentEmployee();
        if (currentEmployee?.type === 'Admin') {
          navigate('/my-profile');
          return;
        }
        goToProfileRoute(currentEmployee.id);
        return;
      } catch {
        // Fallback to list/email match when direct mapping endpoint is unavailable.
      }

      if (!userEmail) {
        if (isAdminUser) {
          navigate('/my-profile');
          return;
        }
        toast.error(t('messages.profileResolveFailed'));
        return;
      }

      const employees = await employeeApi.getAll(1, 200);
      const profile = employees.find(
        (employee) =>
          employee.email?.trim().toLowerCase() === userEmail ||
          employee.officialEmail?.trim().toLowerCase() === userEmail
      );

      if (!profile) {
        if (isAdminUser) {
          navigate('/my-profile');
          return;
        }
        toast.error(t('messages.profileNotFound'));
        return;
      }

      goToProfileRoute(profile.id);
    } catch {
      if (isAdminUser) {
        navigate('/my-profile');
        return;
      }
      toast.error(t('messages.profileNotFound'));
    }
  };

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobile) setOpen(false);
    },
    [navigate, isMobile]
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Toolbar>
          {showNavigation && (
            <IconButton color="inherit" onClick={handleDrawerToggle} edge="start" sx={{ mr: { xs: 1, sm: 2 } }}>
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {user?.companyName || 'HRMS'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasAnyAssignedPermission && <HelpMenu />}
            {hasAnyAssignedPermission && (
              <IconButton color="inherit" onClick={handleBellOpen}>
                <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <Menu
              anchorEl={notificationAnchorEl}
              open={isNotificationOpen}
              onClose={handleBellClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  width: 380,
                  borderRadius: 2,
                  mt: 1,
                  p: 1.5,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                  {t('notifications.title')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleMarkAllRead}
                    disabled={isMarkAllReadLoading || unreadNotificationCount === 0}
                    sx={{ minWidth: 'auto', px: 0, textTransform: 'none', color: 'text.secondary' }}
                  >
                    {t('notifications.markAllRead')}
                  </Button>
                  <IconButton size="small" onClick={handleBellClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
                {isBellLoading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {t('notifications.loading')}
                  </Typography>
                ) : bellNotifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {t('notifications.none')}
                  </Typography>
                ) : (
                  bellNotifications.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => void handleBellNotificationClick(item)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: item.isRead ? 'divider' : 'primary.light',
                        bgcolor: item.isRead ? 'background.paper' : 'rgba(102, 126, 234, 0.08)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: item.isRead ? 'action.hover' : 'rgba(102, 126, 234, 0.14)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '1rem', fontWeight: item.isRead ? 500 : 700, color: 'text.primary' }}>
                        {item.title}
                      </Typography>
                        <Typography
                        sx={{
                          mt: 0.3,
                          color: 'text.secondary',
                          fontWeight: item.isRead ? 400 : 500,
                        }}
                      >
                        {truncateText(item.message, 70)}
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.6, display: 'block', color: 'text.disabled' }}>
                        {item.type} &nbsp; {formatNotificationAge(item.createdAt)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                onClick={() => {
                  handleBellClose();
                  navigate('/notifications');
                }}
              >
                {t('notifications.viewAll')}
              </Button>
            </Menu>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <Avatar src={getUploadedFileUrl(currentUserProfileImage)} sx={{ width: 32, height: 32 }}>
                {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            {hasAnyAssignedPermission && (
              <>
                <MenuItem onClick={handleProfile}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  {t('settings.profile')}
                </MenuItem>
                <MenuItem onClick={handleOpenChangePassword}>
                  <ListItemIcon>
                    <LockIcon fontSize="small" />
                  </ListItemIcon>
                  {t('auth.changePassword')}
                </MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={handleOpenLanguagePreferences}>
              <ListItemIcon>
                <TranslateIcon fontSize="small" />
              </ListItemIcon>
              {t('language.preferred')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('auth.signOut')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Dialog open={languageDialogOpen} onClose={handleCloseLanguagePreferences} maxWidth="xs" fullWidth>
        <DialogTitle>{t('language.preferences')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <LanguagePreferenceSelect size="medium" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLanguagePreferences} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveLanguagePreferences} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={changePasswordOpen} onClose={handleCloseChangePassword} maxWidth="xs" fullWidth>
        <DialogTitle>{t('auth.changePassword')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label={t('auth.currentPassword')}
            type={showCurrent ? 'text' : 'password'}
            fullWidth
            size="small"
            margin="normal"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('auth.newPassword')}
            type={showNew ? 'text' : 'password'}
            fullWidth
            size="small"
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* <TextField
            label="Confirm Password"
            type={showConfirm ? 'text' : 'password'}
            fullWidth
            size="small"
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={
              passwordError ||
              (confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : '')
            }
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          /> */}
            <TextField
            label={t('auth.confirmPassword')}
            type={showConfirm ? 'text' : 'password'}
            fullWidth
            size="small"
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!passwordError || (!!confirmPassword && confirmPassword !== newPassword)}
            helperText={
              passwordError ||
              (confirmPassword && confirmPassword !== newPassword
                ? t('validation.passwordsDoNotMatch')
                : '')
            }
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePassword} color="inherit" disabled={changePasswordLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={changePasswordLoading || !currentPassword || !newPassword || !confirmPassword}
          >
            {changePasswordLoading ? t('common.processing') : t('auth.updatePassword')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer content shared between both variants */}
      {showNavigation && (() => {
        const drawerContent = (
          <>
            <Toolbar />
            <Box
              sx={{
                overflow: 'auto',
                mt: 1,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 4 },
              }}
            >
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Avatar
                  src={getUploadedFileUrl(currentUserProfileImage)}
                  sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: 'primary.main' }}
                >
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Typography variant="subtitle2" fontWeight="bold">
                  {user?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.roleName}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />

              <List disablePadding sx={{ px: 0.5 }}>
                {visibleMenuItems.map((item) => (
                  <React.Fragment key={item.text}>
                    {item.dividerBefore && (
                      <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
                    )}
                    <SidebarMenuItem
                      item={item}
                      level={0}
                      pathname={location.pathname}
                      navigate={handleNavigate}
                      // ─── Accordion props ───
                      // Only pass isOpen/onToggle for items that have children
                      isOpen={item.children ? openMenuKey === item.text : undefined}
                      onToggle={handleMenuToggle}
                    />
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </>
        );

        return isMobile ? (
          <Drawer
            variant="temporary"
            anchor="left"
            open={open}
            onClose={() => setOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        ) : (
          <Drawer
            variant="persistent"
            anchor="left"
            open={open}
            sx={{
              width: open ? drawerWidth : 0,
              flexShrink: 0,
              transition: 'width 0.3s',
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        );
      })()}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: !isMobile && showNavigation && open ? `calc(100% - ${drawerWidth}px)` : '100%',
          transition: 'width 0.3s, margin 0.3s',
          ml: 0,
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};


export default DashboardLayout;
